import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import type { PrivacyPolicy } from '../types/privacy-policy';
import { PrivacyPolicyService } from '../services/privacy-policy.service';
import { getAccessToken, isAuthenticated } from '../utils/authTokens';
import { logoutSession } from '../services/authSession';
import PrivacyPolicyModal from '../components/privacy-policy/PrivacyPolicyModal';

export interface PrivacyPolicyContextType {
  isAccepted: boolean | null;
  isChecking: boolean;
  policy: PrivacyPolicy | null;
  isModalOpen: boolean;
  policyLoading: boolean;
  policyError: string | null;
  checkConsent: (token?: string) => Promise<boolean>;
  acceptPolicy: (value?: string) => Promise<void>;
  rejectPolicy: (reason?: string) => Promise<void>;
  reloadPolicy: () => Promise<void>;
}

const PrivacyPolicyContext = createContext<PrivacyPolicyContextType | undefined>(
  undefined
);

interface PrivacyPolicyProviderProps {
  children: ReactNode;
}

export const PrivacyPolicyProvider: React.FC<PrivacyPolicyProviderProps> = ({
  children,
}) => {
  const [isAccepted, setIsAccepted] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [policy, setPolicy] = useState<PrivacyPolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState<boolean>(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Fetch current policy content for display in modal
   */
  const fetchCurrentPolicy = useCallback(async (token?: string) => {
    setPolicyLoading(true);
    setPolicyError(null);
    try {
      const data = await PrivacyPolicyService.getCurrent(token);
      setPolicy(data);
    } catch (err: any) {
      console.error('[PrivacyPolicy] Error fetching current policy:', err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to load privacy policy content. Please try again.';
      setPolicyError(msg);
    } finally {
      setPolicyLoading(false);
    }
  }, []);

  /**
   * Check consent status for current user.
   * If bool === false, triggers fetchCurrentPolicy and opens blocking modal.
   * If bool === true, grants access.
   */
  const checkConsent = useCallback(
    async (tokenOverride?: string): Promise<boolean> => {
      const token = tokenOverride || getAccessToken();
      if (!token) {
        setIsAccepted(null);
        setIsModalOpen(false);
        return false;
      }

      setIsChecking(true);
      try {
        const status = await PrivacyPolicyService.checkAccepted('current', token);
        if (status.bool === true) {
          setIsAccepted(true);
          setIsModalOpen(false);
          return true;
        } else {
          // Policy has NOT been accepted
          setIsAccepted(false);
          setIsModalOpen(true);
          // Fetch current policy details for the modal
          await fetchCurrentPolicy(token);
          return false;
        }
      } catch (err: any) {
        console.warn('[PrivacyPolicy] checkAccepted warning:', err);
        // If 404, no active policy exists on the server, so bypass blocking
        if (err?.response?.status === 404) {
          setIsAccepted(true);
          setIsModalOpen(false);
          return true;
        }
        // If 401, session is invalid/expired - auth interceptors will handle
        if (err?.response?.status === 401) {
          setIsAccepted(null);
          setIsModalOpen(false);
          return false;
        }
        // For temporary 500 / network errors during initial check, do not permanently lock out the user
        // unless server explicitly returned bool === false.
        setIsAccepted(true);
        setIsModalOpen(false);
        return true;
      } finally {
        setIsChecking(false);
      }
    },
    [fetchCurrentPolicy]
  );

  /**
   * User accepts policy:
   * Sends POST /privacy-policy/{version}/accept with { value: "" }
   */
  const acceptPolicy = useCallback(
    async (value: string = ''): Promise<void> => {
      const token = getAccessToken();
      const version = policy?.version ?? 'current';

      try {
        await PrivacyPolicyService.accept(version, token || undefined, value);
        message.success('Privacy policy accepted successfully.');
        setIsAccepted(true);
        setIsModalOpen(false);
      } catch (err: any) {
        console.error('[PrivacyPolicy] Accept failed:', err);
        const errMsg =
          err?.response?.data?.message ||
          'Failed to record acceptance. Please check your connection and try again.';
        message.error(errMsg);
        throw err;
      }
    },
    [policy]
  );

  /**
   * User rejects policy:
   * Sends POST /privacy-policy/{version}/reject
   * Informs user, logs them out, redirects to /login.
   */
  const rejectPolicy = useCallback(
    async (reason?: string): Promise<void> => {
      const token = getAccessToken();
      const version = policy?.version ?? 'current';

      try {
        await PrivacyPolicyService.reject(version, token || undefined, reason);
      } catch (err: any) {
        console.warn('[PrivacyPolicy] Reject request error (logging out anyway):', err);
      } finally {
        setIsModalOpen(false);
        setIsAccepted(false);
        setPolicy(null);
        message.warning(
          'Privacy policy was declined. Acceptance is required to use this service.'
        );
        await logoutSession();
        navigate('/login', { replace: true });
      }
    },
    [policy, navigate]
  );

  const reloadPolicy = useCallback(async () => {
    const token = getAccessToken();
    await fetchCurrentPolicy(token || undefined);
  }, [fetchCurrentPolicy]);

  // Check consent when user is authenticated and navigating outside /login
  useEffect(() => {
    const token = getAccessToken();
    const isLoginRoute = location.pathname === '/login';

    if (token && !isLoginRoute && isAccepted === null && !isChecking) {
      checkConsent(token);
    } else if (!token) {
      setIsAccepted(null);
      setIsModalOpen(false);
    }
  }, [location.pathname, isAccepted, isChecking, checkConsent]);

  const contextValue: PrivacyPolicyContextType = {
    isAccepted,
    isChecking,
    policy,
    isModalOpen,
    policyLoading,
    policyError,
    checkConsent,
    acceptPolicy,
    rejectPolicy,
    reloadPolicy,
  };

  return (
    <PrivacyPolicyContext.Provider value={contextValue}>
      {children}
      <PrivacyPolicyModal
        open={isModalOpen && Boolean(isAuthenticated())}
        policy={policy}
        loading={policyLoading}
        error={policyError}
        onRetry={reloadPolicy}
        onAccept={acceptPolicy}
        onReject={rejectPolicy}
      />
    </PrivacyPolicyContext.Provider>
  );
};

export const usePrivacyPolicy = (): PrivacyPolicyContextType => {
  const context = useContext(PrivacyPolicyContext);
  if (!context) {
    throw new Error(
      'usePrivacyPolicy must be used within a PrivacyPolicyProvider'
    );
  }
  return context;
};

export default PrivacyPolicyProvider;
